import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TagCloud from '../TagCloud/TagCloud';
import ScheduleEntriesTable from './ScheduleEntriesTable';
import { getPermissions } from '../../utils/getPermissions';

export default class ScheduleForm extends Component {
  state = {
    showErrors: false,
    errors: {
      name: null,
    },
    name: this.props.schedule?.name || '',
    description: this.props.schedule?.description || '',
  };

  componentDidUpdate(prevProps) {
    const { schedule } = this.props;
    const { schedule: prevSchedule } = prevProps;
    const updates = {};

    if (schedule.name !== prevSchedule.name) {
      updates.name = schedule.name;
    }

    if (schedule.description !== prevSchedule.description) {
      updates.description = schedule.description;
    }

    if (Object.keys(updates).length > 0) {
      this.setState(updates);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { showErrors } = this.state;

    if (showErrors) {
      this.validate(nextProps);
    }
  }

  onChange = event => {
    const { onChange } = this.props;

    onChange(event.target.name, event.target.value);
  };

  onChangeTag = tags => {
    const { onChange } = this.props;

    onChange('tags', tags);
  };

  errorClass = error => (error ? 'error' : null);

  validate = nextProps => {
    const props = nextProps || this.props;
    const {
      schedule: { name },
    } = props;
    const errors = { name: null };
    let valid = true;

    // name
    if (!name || !name.trim()) {
      errors.name = 'This field cannot be empty.';
      valid = false;
    } else if (this.isNameInUse(props)) {
      errors.name = 'This name is already in use.';
      valid = false;
    }

    this.setState({ showErrors: true, errors });

    return valid;
  };

  submit = event => {
    const { isNew, onAdd, onUpdate } = this.props;

    event.preventDefault();

    if (!this.validate()) {
      return;
    }

    if (isNew) {
      onAdd();
    } else {
      onUpdate();
    }
  };

  isNameInUse = props => {
    const { schedules, schedule } = props;

    return schedules.some(item => {
      const name = schedule.name.toLowerCase().trim();
      const itemName = item.name.toLowerCase().trim();

      return item.id !== schedule.id && itemName === name;
    });
  };

  getButtons = () => {
    const { auth, isNew, onCancel } = this.props;
    const p = getPermissions(auth);
    const add = (
      <button
        key="1"
        type="submit"
        data-test="button-add"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        Add Schedule
      </button>
    );
    const update = (
      <button
        key="2"
        type="submit"
        data-test="button-update"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        Save Changes
      </button>
    );
    const cancel = (
      <button
        key="3"
        type="button"
        className="px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs text-gray-400 hover:text-black transition-colors border border-transparent hover:border-gray-200"
        onClick={onCancel}
        data-test="button-cancel"
      >
        Cancel
      </button>
    );

    if (!p.writer) {
      return [cancel];
    }

    return isNew ? [add, cancel] : [update, cancel];
  };

  render() {
    const {
      auth,
      folders,
      signs,
      schedule,
      entries,
      onSort,
      onChangeDate,
      onChangeHour,
      onChangeMinute,
      onChangeRepeat,
      onChangeDays,
      onRemoveEntry,
      onClickAddSign,
      onClickAddSequence,
    } = this.props;
    const { name, description, errors } = this.state;
    const p = getPermissions(auth);

    if (!schedule) {
      return null;
    }

    const { tags } = schedule;

    return (
      <div
        id="edit"
        className="form-signs lg:col-span-8 mt-4 bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-200 p-4"
      >
        <form className="space-y-10" onSubmit={this.submit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="relative group" data-test="scheduleform-form-group">
              {this.errorClass(errors.name) && (
                <span className="text-sunrise font-bold  mt-1 mb-1">{errors.name}</span>
              )}
              <label
                htmlFor="schedule-input-name"
                className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Schedule Name
              </label>
              <input
                id="schedule-input-name"
                data-test="input-name"
                type="text"
                name="name"
                value={name}
                onChange={e => this.setState({ name: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!p.writer}
                className="w-full text-xl font-bold px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
              />
            </div>
            <div className="relative group" data-test="scheduleform-form-group">
              <label
                htmlFor="schedule-description"
                className=" text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Description
              </label>
              <textarea
                id="schedule-description"
                data-test="input-desc"
                rows="1"
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                value={description}
                onChange={e => this.setState({ description: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!p.writer}
                name="description"
                className="w-full text-xl font-medium px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none resize-none"
              />
            </div>

            <TagCloud tags={tags} onChange={this.onChangeTag} disabled={!p.writer} />
          </div>
          <div className="clearfix space-x-2" />

          {p.writer && (
            <div className="mt-4 md:mt-2 flex gap-2 justify-end">
              <button
                data-test="add-sequence"
                type="button"
                onClick={onClickAddSequence}
                className="px-5 py-2  !appearance-none !no-underline !cursor-pointer  hover:!text-white bg-btn-primary-form text-white appearance-none rounded-full text-xs font-bold uppercase tracking-widest"
              >
                Add Sequence
              </button>
              {p.atLeastTierPro && (
                <button
                  data-test="add-sign"
                  onClick={onClickAddSign}
                  type="button"
                  className="px-5 py-2 border hover:!text-warmth !cursor-pointer  !appearance-none !no-underline text-warmth border-warmth appearance-none text-dark-night rounded-full text-xs font-bold uppercase tracking-widest"
                >
                  Add Sign
                </button>
              )}
            </div>
          )}

          <h4>Schedule Entries</h4>
          <ScheduleEntriesTable
            auth={auth}
            folders={folders}
            signs={signs}
            data={entries}
            onSort={onSort}
            onChangeDate={onChangeDate}
            onChangeHour={onChangeHour}
            onChangeMinute={onChangeMinute}
            onChangeRepeat={onChangeRepeat}
            onChangeDays={onChangeDays}
            onRemoveEntry={onRemoveEntry}
          />
          <div className="pt-6 flex flex-col sm:flex-row gap-4">{this.getButtons()}</div>
        </form>
      </div>
    );
  }
}

ScheduleForm.propTypes = {
  auth: PropTypes.object.isRequired,
  folders: PropTypes.object.isRequired,
  signs: PropTypes.array.isRequired,
  schedules: PropTypes.array,
  schedule: PropTypes.object,
  entries: PropTypes.array,
  isNew: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onChangeDate: PropTypes.func.isRequired,
  onChangeHour: PropTypes.func.isRequired,
  onChangeMinute: PropTypes.func.isRequired,
  onChangeRepeat: PropTypes.func.isRequired,
  onChangeDays: PropTypes.func.isRequired,
  onRemoveEntry: PropTypes.func.isRequired,
  onClickAddSign: PropTypes.func.isRequired,
  onClickAddSequence: PropTypes.func.isRequired,
};
